/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useEffect, ReactElement, useContext, useRef, RefObject, useMemo } from 'react';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import {
  ViewportContext,
  ContentContext,
  FormValuesContext,
  FocusContext,
  SelectDestinationNameContext,
} from '../../index.contexts';
import { DestinationProps } from './destination.types';
import { StyledDestination } from './destination.styles';
import Popup from '../../atoms/popup/popup';
import PopupHeader from '../../atoms/popupHeader/popupHeader';
import PopupMain from '../../atoms/popupMain/popupMain';
import { setDestination, setDestinationHiddenFields } from '../../../store/store.actions';
import { DESTINATION, DATES, DESTINATION_HIDDEN_FIELDS } from '../../../constants/StoreConstants';
import { VARIATION_DESKTOP, VARIATION_TABLET } from '../../../constants/ApplicationConstants';
import CommonUtils from '../../../utils/CommonUtils';
import { SuggestedPlacesNode } from '../../index.types';
import { useApolloClient } from '@apollo/client';
import { phoenixShopSuggestedPlacesQuery } from '../../../../../../molecules/SearchField/queries';
import clsx from 'clsx';
import { usePageContext } from '../../../../../../context/PageContext';
import RecentViewAndSearch from '../recentViewAndSearch/recentViewAndSearch';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { ADDRESS_TYPE_COUNTRY } from '../../../../../../constants/lib/constants';
import { CHINA_REGION_CODE } from '../../../../../../modules/constants/HotelDirectory.constants';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>;
  }
}

const Destination: FC<DestinationProps> = ({ onCancel, onChange: changeMobileState, isSearchPage = false }) => {
  const { placeholderTextMobile, placeholderTextDesktop, destination, noResultFound, destinationSearchResult, cancel } =
    useContext(ContentContext);
  const { sessionData, setSessionData, requestId, currentLocale, pageState, isAuthorMode } = usePageContext();
  // const { interactionsSelected, setInteractionsSelected } = useSessionUpdateContext();
  let requestID: string;
  if (requestId) {
    requestID = requestId;
  }
  const { formValues, setFormValues, setIsPopupOpen, activePopup, setActivePopup } = useContext(FormValuesContext);
  const { setIsAutoSuggestSelected, setIsRecentSearchSelected } = useContext(SelectDestinationNameContext);
  const { setFocusComp, setIsUserInteractWithComp, setIsUserEditSearchForm } = useContext(FocusContext);
  const searchText = formValues?.[DESTINATION]?.displayText;
  const isMobileView = useContext(ViewportContext);
  // const { log } = logger({})('Destination');
  // flag for openning the popup
  const [openPopup, setOpenPopupState] = useState(false);
  // value of user typed in
  const [inputValue, setInputValue] = useState<string>('');
  const [optionsList, setOptionsList] = useState<SuggestedPlacesNode[] | string[]>([]);
  // value of input field, from choosing the suggestion
  const [value, setValue] = useState<string | null>(searchText ? searchText : null);
  const [showRecentViewed, setRecentViewed] = useState(true);
  const [timerList, setTimerList] = useState<ReturnType<typeof setTimeout>[]>([]);
  const formId = 'search-destination';
  const compRef = useRef<HTMLDivElement>(null);
  // use ref to access the current inputValue during setTimeout call for fetching auto suggestions
  const inputValueRef = useRef(inputValue);
  // use ref to access the screen-reader-text div
  const screenReaderTextRef = useRef<HTMLDivElement>(null);
  const [destinationOnFocus, setDestinationOnFocus] = useState(false);
  const [variation, setVariation] = React.useState<string>(CommonUtils.getVariation());
  const [popupWidth, setPopupWidth] = useState('');
  const [showSearchListView, setShowSearchListView] = useState(false);
  const [suggestionListData, setSuggestionListData] = useState<{
    suggestedPlaces: {
      edges: SuggestedPlacesNode[];
      total: number;
    };
  }>();

  const skipQuery = useMemo(() => {
    return !!pageState?.authorMode || !!isAuthorMode;
  }, [pageState, isAuthorMode]);

  const client = useApolloClient();
  async function getSuggestionList(inputValues: any) {
    const { data } = await client.query({
      query: phoenixShopSuggestedPlacesQuery,
      ...inputValues,
    });
    if (data) {
      setSuggestionListData(data);
    }
  }

  // Utility to set variation based on true value
  const setSuccessVariation = (flag: boolean, variation: string): void => {
    if (flag) {
      setVariation(variation);
    }
  };

  // update variation based on viewport size change
  useEffect(() => {
    // set viewport change event and return callback function to clear events
    const updateViewportHandler = CommonUtils.addRemoveResizeHandler(setSuccessVariation);

    return (): void => {
      // remove change event listner on component unmount
      updateViewportHandler();
    };
  }, []);

  // Close popup when another popup becomes active
  useEffect(() => {
    if (activePopup !== null && activePopup !== 'destination' && openPopup) {
      setOpenPopupState(false);
    }
  }, [activePopup]);

  const setPopupOpen = (val: boolean): void => {
    setOpenPopupState(val);
    // to scroll search form to top
    setIsPopupOpen?.(val);
    // Update activePopup state for popup coordination
    if (val) {
      setActivePopup?.('destination');
    } else {
      setActivePopup?.(null);
    }
  };
  const clearTimer = (timers: ReturnType<typeof setTimeout>[]): void => {
    if (timers.length) {
      for (let i = timers.length - 1; i >= 0; i--) {
        clearTimeout(timers[i]);
      }
      setTimerList([]);
    }
  };

  /**
   * making an Aries API call to get auto-suggestion data based on user input value
   * return a callback function using response data
   * @param input: the current input value in text field;
   * @param Uuid: uuid generated when component is rendered
   * @param callback: callback function once there is response coming back from API call
   */

  /**
   * Method to check if the unsuccessful user journey alert should be displayed
   * @returns boolean
   */
  function isUnsuccessfulUserJourney(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params?.has('unsuccessfulSell') && params?.get('unsuccessfulSell') === 'true';
  }
  useEffect(() => {
    const displayText = formValues?.[DESTINATION]?.displayText ?? null;
    //displayText !== value - This condition is to make sure, Only different Value updates here.
    if (displayText && displayText !== value) {
      //sets the value if there is no UnsuccessfulSell param
      !isUnsuccessfulUserJourney() && setValue(displayText);
      setInputValue(displayText);
    }
  }, [formValues]);

  // setting screen reader text and removing after 100ms
  // so that SR can announce updated result everytime
  const setScreenReaderText = (elm: Element, text: string): void => {
    if (elm) {
      elm.textContent = text;
      setTimeout(() => {
        elm.textContent = '';
      }, 100);
    }
  };

  useEffect(() => {
    // update current valRef with new inputValue
    inputValueRef.current = inputValue;

    // Delaying API call till the input value finishes updating
    if (inputValue && inputValue !== value) {
      clearTimer(timerList);
      const timer = setTimeout(() => {
        if (!skipQuery) {
          getSuggestionList({
            variables: {
              query: inputValue,
            },
            context: {
              headers: {
                // Any header, including x-request-id, can be passed ui-libraryin args with query.
                // If you don't pass it in the authLink will generate a random ID.
                'x-request-id': requestID,
                'accept-language': currentLocale,
              },
            },
          });
        }
      }, 500);
      setTimerList([...timerList, timer]);
      setRecentViewed(false);
    } else {
      // cancel current fetch timeout tiemr if user delete the input value
      clearTimer(timerList);

      // clear options list and show recently viewed when input value is cleared
      setOptionsList([]);
      setRecentViewed(true);
    }
    // value is the val attribute of input field
  }, [inputValue, value]);

  useEffect(() => {
    const srElm = screenReaderTextRef.current;
    if (suggestionListData && suggestionListData.suggestedPlaces !== undefined) {
      if (suggestionListData.suggestedPlaces.total === 0) {
        setFormValues(
          setDestination({
            displayText: '',
            pid: '',
          })
        );
        if (formValues?.[DESTINATION_HIDDEN_FIELDS]?.placeId) {
          setFormValues(setDestinationHiddenFields({}));
        }
        setOptionsList([]);
        // if there's no search results, screen reader should announce
        // no results found.
        if (srElm) {
          setScreenReaderText(srElm, noResultFound);
        }
      } else if (suggestionListData.suggestedPlaces.total > 0 && inputValueRef.current) {
        // use valRef.current to check the current inputValue at the end of timeout
        // if inputValue is cleared, do not update optionsList
        // if there is auto-suggest, hide top destinations/search history
        setRecentViewed(false);
        if (suggestionListData.suggestedPlaces.edges) {
          setOptionsList(suggestionListData.suggestedPlaces.edges);
          //We are checking Primary description or complete description to select placeid.
          if (
            suggestionListData.suggestedPlaces.edges[0]?.node?.primaryDescription?.toLowerCase() ===
              inputValue.toLowerCase() ||
            suggestionListData.suggestedPlaces.edges[0]?.node?.description?.toLowerCase() === inputValue.toLowerCase()
          ) {
            setFormValues(
              setDestinationHiddenFields({
                placeId: suggestionListData.suggestedPlaces.edges[0]?.node?.placeId,
              })
            );
          }
          // screen reader will announce how many results are present
          if (srElm) {
            setScreenReaderText(
              srElm,
              destinationSearchResult.replace('{x}', `${suggestionListData.suggestedPlaces.total}`)
            );
          }
        }
      }
    }
  }, [suggestionListData]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stringifyOption = (option: SuggestedPlacesNode | Record<string, string> | any): string => {
    try {
      return JSON.stringify(isSearchPage ? option.node : option);
    } catch {
      return '';
    }
  };

  const optionDescription = (option: string): string => {
    try {
      const parsedOption = JSON.parse(option);
      // below check is required because JSON.parse("33").something
      // return undefined rather error
      if (typeof parsedOption === 'object') {
        return parsedOption.description;
      }
      throw new Error();
    } catch {
      return option;
    }
  };

  // update the input field with recent search value and close the popup
  const updateRecentValue = (
    recentValue: string | number,
    pid?: string | number,
    secondaryDescription?: string | number,
    city?: string,
    state?: string,
    country?: string,
    destinationType?: string
  ): void => {
    if (typeof recentValue === 'string') {
      // shift focus to calendar after selection
      setFocusComp(DATES);

      // to remove "focused" class
      setDestinationOnFocus(false);

      setValue(recentValue);
      setIsRecentSearchSelected(true);
      setIsAutoSuggestSelected(false);
      setPopupOpen(false);
      changeMobileState?.();
      const isStateSearch = !city && state ? true : false;
      const isCountrySearch = !city && !state && country ? true : false;
      if (pid) {
        updateFormValues(recentValue, pid, secondaryDescription, destinationType);
      } else {
        geoCodeAddress(recentValue, isStateSearch, isCountrySearch)
          .then(placeId => {
            updateFormValues(recentValue, placeId, secondaryDescription, destinationType);
          })
          .catch(() => {
            updateFormValues(recentValue, pid, secondaryDescription, destinationType);
          });
      }
    }
  };

  useEffect(() => {
    //sets the destination value if unsuccessful user journey alert being displayed
    if (isUnsuccessfulUserJourney()) {
      const sessionObject = sessionData?.cacheData?.data ?? sessionData?.data;
      // Read prop_name from AriesCommon session object, else fail safe to reading it from Datalayer object
      const prop_name = sessionObject?.AriesCommon?.prop_name || window?.dataLayer?.['prop_name'];
      setValue(prop_name);
    } else {
      /*
      Updates the destination field value with the searchCenter 
      address field, if destination query triggers.
      */
      eventUtil.on('SearchResultsData', response => {
        updateFormValues(response?.address);
        //Update form values with search center data and call the function, only when latitude and longitude is present in search center.
        if (response?.latitude && response?.longitude) {
          updateFormValuesWithSearchCenter(response?.latitude, response?.longitude);
        }
        // Update the session data with the address details received from searchCenter
        if (
          sessionData?.data?.AriesSearch?.searchCriteria?.address &&
          response?.latitude &&
          response?.longitude &&
          response?.address
        ) {
          const destinationAddress = {
            city: '',
            cityPopulation: 0,
            cityPopulationDensity: 0,
            country: '',
            countryShortName: '',
            destination: response.address,
            destinationType: '',
            googlePlaceAddress: '',
            googlePlaceAddressLocality: '',
            googlePlaceAddressName: '',
            googlePlaceAddressTypes: '',
            googlePlaceWebsite: '',
            latitude: response.latitude,
            location: '',
            longitude: response.longitude,
            postalCode: '',
            region: '',
            stateProvince: '',
            stateProvinceDisplayName: '',
          };
          sessionData.data.AriesSearch.searchCriteria.address = destinationAddress;
          setSessionData(sessionData);
        }
      });
    }
  }, []);

  // To call geoCoder and get placeid for destinations from recent searches
  function geoCodeAddress(location: string, isStateSearch = false, isCountrySearch = false): Promise<string> {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: location }, (results: any, status: any) => {
        if (status === 'OK') {
          if (results.length) {
            let placeId;
            if (results.length > 1) {
              for (let addressCount = 0; addressCount < results.length; addressCount++) {
                //if its a country search and country is CHINA , resolve placeId for CN , this helps to handle cases where we get other country code such as TW, MO, HK
                if (
                  isCountrySearch &&
                  results[addressCount]?.address_components?.[0]?.short_name === CHINA_REGION_CODE
                ) {
                  placeId = results[addressCount].place_id;
                  break;
                } else if (
                  isStateSearch &&
                  !isCountrySearch &&
                  !results[addressCount]?.types?.includes(ADDRESS_TYPE_COUNTRY)
                ) {
                  placeId = results[addressCount].place_id;
                  break;
                }
              }
            }
            placeId = placeId || results[0].place_id;
            resolve(placeId);
          } else {
            reject(new Error('No results found'));
          }
        } else {
          reject(new Error(`Geocoder failed due to: ${status}`));
        }
      });
    });
  }

  const closePopup = (): void => {
    setPopupOpen(false);
  };

  const onDestinationClear = (): void => {
    setValue('');
    setInputValue('');
    setOpenPopupState(true);
    setRecentViewed(true);
    setFormValues(
      setDestination({
        displayText: '',
        pid: '',
      })
    );
    setFormValues(setDestinationHiddenFields({}));
  };

  const updateFormValues = (
    val: string,
    pid?: string | number,
    secondaryDescription?: string | number,
    destinationType?: string
  ): void => {
    if (formValues?.destination?.displayText !== val) {
      // setInteractionsSelected({ ...interactionsSelected, destination: true });
      setFormValues(
        setDestination({
          ...(formValues?.[DESTINATION] ?? {}),
          displayText: val,
          pid,
          secondaryDescription,
        })
      );
      setFormValues(
        setDestinationHiddenFields({
          destinationType: destinationType,
        })
      );
    } else {
      setFormValues(
        setDestination({
          displayText: val,
          pid,
          secondaryDescription,
        })
      );
    }
    setIsUserEditSearchForm(true);
  };

  //function to update form values with search center data
  const updateFormValuesWithSearchCenter = (latitude: string, longitude: string): void => {
    setFormValues(
      setDestinationHiddenFields({
        ...(formValues?.[DESTINATION_HIDDEN_FIELDS] ?? {}),
        latitude: latitude,
        longitude: longitude,
      })
    );
  };

  // function to detect of destination component is on focus by checking where the click event happens
  const useFocusInsideComp = (ref: RefObject<HTMLDivElement>): void => {
    useEffect(() => {
      // const autoCompleteInput: HTMLElement | null = ref.current?.querySelector('input') as HTMLElement;
      const handleClickInside = (event: Event): void => {
        if (ref.current?.contains(event.target as Element)) {
          // if (isMobileView) {
          //   // sync focus behavior in mobile view
          //   autoCompleteInput?.focus();
          // }
          setDestinationOnFocus(true);
        } else {
          setDestinationOnFocus(false);
          setPopupOpen(false);
        }
      };

      document.addEventListener('focusin', handleClickInside);
      return (): void => {
        document.removeEventListener('focusin', handleClickInside);
      };
    }, [ref]);
  };
  useFocusInsideComp(compRef);

  // Based on search container width assign popup width on initial load/resize
  useEffect(() => {
    const handlePopupWidth = (): void => {
      //No dom manipulation in this line
      const parentContainerWidth = document.querySelector('.search-primary')?.getBoundingClientRect().width;
      const popupWidth = parentContainerWidth ? `${parentContainerWidth}px` : '100%';
      setPopupWidth(popupWidth);
    };
    handlePopupWidth();
    window.addEventListener('resize', handlePopupWidth, { passive: true });
    return (): void => {
      window.removeEventListener('resize', handlePopupWidth);
    };
  }, []);

  useEffect(() => {
    if (!openPopup) {
      setShowSearchListView(false);
    }
  }, [openPopup]);

  let selectedFromAutoComplete = false;
  const popUpHeaderContent = (): React.ReactNode => {
    return (
      <PopupHeader
        inputId={formId}
        labelIcon="icon-location"
        labelText={destination}
        cancelLabel={cancel}
        cancelClickHandler={isMobileView && onCancel ? onCancel : undefined}
        removeScreenReaderClass={true}
      >
        <Autocomplete
          id={formId}
          className={clsx(
            inputValue || value ? 'dirty' : '',
            destinationOnFocus && openPopup ? 'focused' : '',
            destinationOnFocus ? 'show-border' : ''
          )}
          options={optionsList?.map(stringifyOption)}
          filterOptions={(x): string[] => x}
          getOptionLabel={optionDescription}
          disablePortal={true}
          openOnFocus={true}
          clearOnBlur={false}
          forcePopupIcon={false}
          aria-labelledby="document_search_form_container_el_ref"
          aria-controls="search-destination-popup"
          onOpen={(): void => {
            setPopupOpen(true);
            setIsUserInteractWithComp(true);
          }}
          onClose={(): void => {
            // Secondary Tab Show functionality
            if (
              formValues?.[DESTINATION]?.displayText !== inputValue &&
              inputValue.trim() &&
              !selectedFromAutoComplete
            ) {
              updateFormValues(inputValue);
            }
            selectedFromAutoComplete = false;
          }}
          aria-expanded={openPopup}
          value={value}
          onChange={(_event, newValue: string | null, reason: string): void => {
            // onChange will be triggered when an auto-suggest option is chosen or a chosen option is deleted;
            // thus only call the following when newValue exists, aka an option is chosen
            if (newValue) {
              setValue(newValue);
              setIsAutoSuggestSelected(true);
              setIsRecentSearchSelected(false);
              setPopupOpen(false);
              setOptionsList([]);

              selectedFromAutoComplete = true;
              const PID = JSON.parse(newValue).placeId;

              try {
                changeMobileState?.();
                updateFormValues(JSON.parse(newValue).description, PID, JSON.parse(newValue).secondaryDescription);
              } catch {
                changeMobileState?.();
                updateFormValues(newValue, PID);
              }

              setDestinationOnFocus(false);
              // shift focus to calendar after selection
              // setFocusComp(DATES);
            }
            // reset once textfield is cleared up from clear button
            if (reason === 'clear') {
              setValue(null);
              setInputValue('');
            }
          }}
          onInputChange={(event, newInputValue: string): void => {
            // only to catch the event triggered by typing whose type is 'change';
            // exclude the input change from choosing an option from dropdown
            if (event?.type === 'change') {
              const trimmedNewInput = newInputValue.trim();
              setPopupOpen(true);
              setInputValue(trimmedNewInput);
            }

            // Reset placeid, once user make changes to destination field.
            if (newInputValue !== value) {
              setFormValues(setDestinationHiddenFields({}));
            }

            // reset once textfield is cleared up from keyboard
            if (newInputValue === '' && event !== null) {
              setValue(null);
              setInputValue('');
              // clear optionsList when inputValue is empty
              setOptionsList([]);
              if (!isMobileView) {
                updateFormValues('', '', ''); // setting input text and PID empty (for Hidden fields)
              }
              setFormValues(
                setDestination({
                  displayText: '',
                  pid: '',
                })
              );
            }
          }}
          renderInput={(params): ReactElement => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {value && (
                      <span
                        tabIndex={0}
                        role="button"
                        aria-label="Clear"
                        className={clsx(openPopup ? 'icon-m icon-cancel' : 'remove-icon')}
                        onClick={() => onDestinationClear()}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.keyCode === 13) {
                            onDestinationClear();
                          }
                        }}
                      />
                    )}
                  </>
                ),
              }}
              name="destinationAddress.destination"
              {...(!value
                ? {
                    placeholder:
                      variation !== VARIATION_DESKTOP && variation !== VARIATION_TABLET
                        ? (placeholderTextMobile as string)
                        : (placeholderTextDesktop as string),
                  }
                : {})}
              variant="standard"
              fullWidth
              value={value}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={isMobileView ? true : false}
              onFocus={(): void => {
                setDestinationOnFocus(true);
                setPopupOpen(true);

                // when focus on empty text field, show recent viewed in popup
                if (value) {
                  setRecentViewed(false);
                } else {
                  setRecentViewed(true);
                }

                // reset focus when destination is invoked
                setFocusComp('');
              }}
            />
          )}
          renderOption={(option): ReactElement => {
            const candidate = JSON.parse(option);
            return (
              <p className="t-font-m">
                {candidate.primaryDescription}
                <span>{candidate.secondaryDescription ? ', ' + candidate.secondaryDescription : ''}</span>
              </p>
            );
          }}
        />
        {isMobileView ? (
          <button type="submit" style={{ display: 'none' }}>
            .
          </button>
        ) : (
          ''
        )}
      </PopupHeader>
    );
  };

  return (
    // detect when click outside the component and close the popup
    <StyledDestination ref={compRef}>
      <Popup
        popupId="search-destination-popup"
        handleBlur={!isMobileView}
        show={openPopup || isMobileView}
        className={clsx(
          openPopup || isMobileView ? 'show-popup' : '',
          openPopup && showSearchListView ? 'dest-popup' : '',
          !isMobileView
            ? 'search-page-destination custom-search-popup'
            : 'search-page-destination-mobile-popup custom-search-popup-mobile',
          isMobileView ? 'search-page-destination-mobile-popup custom-search-popup-mobile' : '',
          !isMobileView && openPopup ? 'current-loc-popup' : ''
        )}
        popupOpenState={openPopup}
        setPopupOpenState={setOpenPopupState}
        width={popupWidth}
      >
        {isMobileView ? (
          <form
            action=""
            method="get"
            onSubmit={(event: React.SyntheticEvent): void => {
              event.preventDefault();
              if (inputValue !== '') {
                updateFormValues(inputValue);
              }
              changeMobileState?.();
            }}
          >
            {popUpHeaderContent()}
          </form>
        ) : (
          popUpHeaderContent()
        )}
        <div aria-live="assertive" ref={screenReaderTextRef} className="screen-reader-text sr-only" />
        {showRecentViewed && openPopup && (
          <PopupMain className="show-popup-scrollable custom-scrollbar">
            <RecentViewAndSearch
              updateInputValue={updateRecentValue}
              updateSearchView={(val: boolean): void => setShowSearchListView(val)}
              showListView={showSearchListView}
              closePopup={closePopup}
            />
          </PopupMain>
        )}
      </Popup>
    </StyledDestination>
  );
};

export default Destination;
